import { Component } from "../lib/component.ts";
import { claimInert, releaseInert } from "../lib/inert-stack.ts";
import { resampleToolbar } from "../lib/resample-toolbar.ts";
import { lockScroll, unlockScroll } from "../lib/scroll-lock.ts";

interface ModalPanelRefs {
  backdrop?: HTMLElement;

  content?: HTMLElement;

  close?: HTMLButtonElement;
}

/**
 * A slide-in overlay panel component.
 *
 * Place a `<template>` inside to define the content. Content is cloned on
 * first open so custom elements initialize properly.
 *
 * @example
 * <button data-modal-trigger="my-panel">Open</button>
 * <modal-panel id="my-panel" direction="bottom">
 *   <template>
 *     <button ref="close" aria-label="Close">×</button>
 *     <p>Panel content</p>
 *   </template>
 * </modal-panel>
 */
class ModalPanel extends Component<ModalPanelRefs> {
  static #stack: ModalPanel[] = [];

  #previouslyFocused: Element | null = null;

  #contentInitialized = false;

  #openedViaHash = false;

  #openFallback: ReturnType<typeof setTimeout> | null = null;

  connectedCallback(): void {
    super.connectedCallback();

    document.addEventListener("click", this.#handleTriggerClick, {
      signal: this.signal,
    });
    document.addEventListener("keydown", this.#handleKeydown, {
      signal: this.signal,
    });
    window.addEventListener("hashchange", this.#handleHashChange, {
      signal: this.signal,
    });

    if (this.#hashMatches()) {
      this.#openedViaHash = true;
      requestAnimationFrame(() => this.open());
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    const wasOpen = this.isOpen;
    ModalPanel.#removeFromStack(this);
    if (wasOpen) {
      this.#unlockScroll();
      releaseInert(this.id);
    }
  }

  get direction(): string {
    return this.getAttribute("direction") || "bottom";
  }

  set title(value: string) {
    const heading = this.refs.content?.querySelector("h1, h2, h3, h4, h5, h6, [role='heading']");
    if (!heading) return;

    const backButton = heading.querySelector(".modal-panel-header__back-button");
    if (backButton) {
      const back = backButton.querySelector(".modal-panel-header__back");
      const labelPrefix = backButton.getAttribute("aria-label")?.split(",")[0];
      backButton.textContent = value;
      if (back) backButton.prepend(back);
      if (labelPrefix) backButton.setAttribute("aria-label", `${labelPrefix}, ${value}`);
    } else {
      heading.textContent = value;
    }
  }

  get isOpen(): boolean {
    return this.hasAttribute("open");
  }

  #handleTriggerClick = (event: MouseEvent): void => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest(`[data-modal-trigger="${this.id}"]`);
    if (trigger) {
      event.preventDefault();
      this.open();
    }
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Escape" && this.isOpen && ModalPanel.#stack.at(-1) === this) {
      event.preventDefault();
      this.close();
    }
  };

  #hashMatches(): boolean {
    return this.id !== "" && location.hash === `#${this.id}`;
  }

  #handleHashChange = (): void => {
    if (this.#hashMatches()) {
      this.#openedViaHash = true;
      this.open();
    } else if (this.isOpen && this.#openedViaHash) {
      this.close();
    }
  };

  #handleBackdropClick = (event: MouseEvent): void => {
    if (event.target === this.refs.backdrop) {
      this.close();
    }
  };

  // If the modal has a back button, the user can dismiss just this modal
  // with it, so "Close" closes the entire stack. Without a back button,
  // "Close" is the only dismiss action, so it closes only this modal.
  #handleCloseClick = (): void => {
    if (this.refs.content?.querySelector("[data-back]")) {
      ModalPanel.closeAll();
    } else {
      this.close();
    }
  };

  static closeAll(): void {
    const panels = [...ModalPanel.#stack].reverse();
    for (const panel of panels) {
      panel.close();
    }
  }

  #handleContentClick = (event: MouseEvent): void => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest("[data-back]")) {
      this.close();
    } else if (event.target.closest("[data-close-modal]")) {
      ModalPanel.closeAll();
    }
  };

  open(): void {
    if (this.isOpen) return;

    this.style.display = "block";
    this.#initializeContent();

    if (!this.refs.content || !this.refs.backdrop) {
      return;
    }

    this.#previouslyFocused = document.activeElement;

    // Force reflow so initial transform state is applied before transitioning
    void this.refs.content.offsetHeight;

    const topModal = ModalPanel.#stack.at(-1);
    if (topModal?.direction === this.direction) {
      this.toggleAttribute("data-stacked", true);
    }

    this.dispatchEvent(new CustomEvent("before-open", { bubbles: true }));

    ModalPanel.#stack.push(this);
    this.setAttribute("open", "");
    this.setAttribute("aria-hidden", "false");
    this.#lockScroll();
    claimInert(this.id, this, { skip: this.getAttribute("inert-skip") ?? undefined });

    const firstFocusable = this.refs.content.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (firstFocusable instanceof HTMLElement) {
      firstFocusable.focus({ preventScroll: true });
    } else {
      this.refs.content.focus({ preventScroll: true });
    }

    const resampleAfterOpen = () => {
      this.refs.backdrop?.removeEventListener("transitionend", onOpenEnd);
      if (this.#openFallback !== null) {
        clearTimeout(this.#openFallback);
        this.#openFallback = null;
      }
      if (!this.isOpen) return;
      resampleToolbar({ bottom: "var(--white-100)" });
    };
    const onOpenEnd = (event: TransitionEvent) => {
      if (event.propertyName === "opacity") resampleAfterOpen();
    };
    this.refs.backdrop?.addEventListener("transitionend", onOpenEnd);
    this.#openFallback = setTimeout(resampleAfterOpen, 350);

    this.dispatchEvent(new CustomEvent("open", { bubbles: true }));
  }

  close(): void {
    if (!this.isOpen) return;

    if (this.#openFallback !== null) {
      clearTimeout(this.#openFallback);
      this.#openFallback = null;
    }

    this.style.display = "block";
    ModalPanel.#removeFromStack(this);
    this.removeAttribute("open");
    this.setAttribute("aria-hidden", "true");

    if (this.#hashMatches()) {
      history.replaceState(null, "", location.pathname + location.search);
    }
    this.#openedViaHash = false;

    const cleanup = () => {
      this.removeAttribute("data-stacked");
      this.#unlockScroll();
      releaseInert(this.id);

      if (this.#previouslyFocused instanceof HTMLElement) {
        this.#previouslyFocused.focus();
      }
      this.#previouslyFocused = null;

      this.style.removeProperty("display");
      this.dispatchEvent(new CustomEvent("close", { bubbles: true }));
    };

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName === "opacity") {
        this.refs.backdrop?.removeEventListener("transitionend", handleTransitionEnd);
        clearTimeout(fallbackTimeout);
        cleanup();
      }
    };

    // Fallback for reduced motion or missing transitions
    const fallbackTimeout = setTimeout(cleanup, 350);

    this.refs.backdrop?.addEventListener("transitionend", handleTransitionEnd);
  }

  #initializeContent(): void {
    if (this.#contentInitialized) return;

    const template = this.querySelector("template");
    if (!template) return;

    const backdrop = document.createElement("div");
    backdrop.className = "modal-panel__backdrop";
    backdrop.setAttribute("ref", "backdrop");

    const content = document.createElement("div");
    content.className = "modal-panel__content";
    content.setAttribute("ref", "content");
    content.setAttribute("role", "dialog");
    content.setAttribute("aria-modal", "true");
    content.setAttribute("tabindex", "-1");
    content.appendChild(template.content.cloneNode(true));

    // Link dialog to its heading for screen readers
    const heading = content.querySelector("h1, h2, h3, h4, h5, h6, [role='heading']");
    if (heading) {
      if (!heading.id) {
        heading.id = `${this.id}-heading`;
      }
      content.setAttribute("aria-labelledby", heading.id);
    }

    this.appendChild(backdrop);
    this.appendChild(content);

    this.refs.backdrop = backdrop;
    this.refs.content = content;
    const closeEl = content.querySelector('[ref="close"]');
    if (closeEl instanceof HTMLButtonElement) {
      this.refs.close = closeEl;
    }

    backdrop.addEventListener("click", this.#handleBackdropClick, {
      signal: this.signal,
    });
    this.refs.close?.addEventListener("click", this.#handleCloseClick, {
      signal: this.signal,
    });
    content.addEventListener("click", this.#handleContentClick, {
      signal: this.signal,
    });

    this.#contentInitialized = true;
  }

  #lockScroll(): void {
    lockScroll(this.id);
  }

  #unlockScroll(): void {
    unlockScroll(this.id);
  }

  static #removeFromStack(panel: ModalPanel): void {
    const index = ModalPanel.#stack.indexOf(panel);
    if (index !== -1) ModalPanel.#stack.splice(index, 1);
  }
}

if (!customElements.get("modal-panel")) {
  customElements.define("modal-panel", ModalPanel);
}
